// ===== BEGIN FILE: src/components/report-v2/sections/PlanetDetailsSection.tsx =====
/**
 * Planet details section with complete planetary positions table
 */

import React from 'react';
import { Box, Card, Typography, Sheet, Chip } from '@mui/joy';
import { ReportSection } from '../ReportSection';
import type { ReportSectionProps } from '../types';

export const PlanetDetailsSection: React.FC<ReportSectionProps> = ({ data, onNavigate }) => {
  const { planets } = data;

  // Planet order for display
  const planetOrder = ['Sun', 'Moon', 'Mars', 'Mercury', 'Jupiter', 'Venus', 'Saturn', 'Rahu', 'Ketu'];
  const sortedPlanets = [...planets].sort((a, b) => {
    return planetOrder.indexOf(a.name) - planetOrder.indexOf(b.name);
  });

  // Color coding for dignity
  const getDignityColor = (dignity: string) => {
    switch (dignity.toLowerCase()) {
      case 'exalted':
        return 'success';
      case 'own sign':
      case 'moolatrikona':
        return 'primary';
      case 'friend':
        return 'neutral';
      case 'debilitated':
        return 'danger';
      case 'enemy':
        return 'warning';
      default:
        return 'neutral';
    }
  };

  // Get house name
  const getHouseName = (house: number): string => {
    const houseNames: Record<number, string> = {
      1: 'Self & Personality',
      2: 'Wealth & Family',
      3: 'Siblings & Courage',
      4: 'Home & Mother',
      5: 'Children & Creativity',
      6: 'Health & Enemies',
      7: 'Partnership & Marriage',
      8: 'Transformation & Longevity',
      9: 'Fortune & Dharma',
      10: 'Career & Status',
      11: 'Gains & Aspirations',
      12: 'Spirituality & Losses',
    };
    return houseNames[house] || '';
  };

  // Planet symbols
  const getPlanetSymbol = (name: string): string => {
    const symbols: Record<string, string> = {
      Sun: '☉',
      Moon: '☽',
      Mars: '♂',
      Mercury: '☿',
      Jupiter: '♃',
      Venus: '♀',
      Saturn: '♄',
      Rahu: '☊',
      Ketu: '☋',
    };
    return symbols[name] || '';
  };

  return (
    <ReportSection
      title="Planetary Positions"
      icon="🪐"
      pageBreakBefore
      onNavigate={onNavigate ? () => onNavigate('calculator') : undefined}
      navigateLabel="View Planet Details →"
      navigateTab="calculator"
    >
      <Box sx={{ mb: 3 }}>
        <Typography level="body-md" sx={{ color: 'text.secondary' }}>
          Complete planetary positions at the time of birth. Each planet's placement in sign, house,
          and nakshatra reveals specific life themes and karmic patterns.
        </Typography>
      </Box>

      {/* Planets table */}
      <Sheet
        variant="outlined"
        sx={{
          borderRadius: 'sm',
          overflow: 'auto',
          '@media print': {
            overflow: 'visible',
          }
        }}
      >
        <table
          style={{
            width: '100%',
            borderCollapse: 'collapse',
            fontSize: '0.8rem', // Reduced from 0.875rem
          }}
        >
          <thead>
            <tr style={{ background: 'linear-gradient(135deg, rgba(91, 33, 182, 0.1), rgba(245, 158, 11, 0.1))' }}>
              <th style={{ padding: '8px 12px', textAlign: 'left', fontWeight: 600, borderBottom: '2px solid #E0E0E0' }}>
                Planet
              </th>
              <th style={{ padding: '8px 12px', textAlign: 'left', fontWeight: 600, borderBottom: '2px solid #E0E0E0' }}>
                Sign
              </th>
              <th style={{ padding: '8px 12px', textAlign: 'center', fontWeight: 600, borderBottom: '2px solid #E0E0E0' }}>
                House
              </th>
              <th style={{ padding: '8px 12px', textAlign: 'right', fontWeight: 600, borderBottom: '2px solid #E0E0E0' }}>
                Degree
              </th>
              <th style={{ padding: '8px 12px', textAlign: 'left', fontWeight: 600, borderBottom: '2px solid #E0E0E0' }}>
                Nakshatra
              </th>
              <th style={{ padding: '8px 12px', textAlign: 'center', fontWeight: 600, borderBottom: '2px solid #E0E0E0' }}>
                Pada
              </th>
              <th style={{ padding: '8px 12px', textAlign: 'left', fontWeight: 600, borderBottom: '2px solid #E0E0E0' }}>
                Dignity
              </th>
              <th style={{ padding: '8px 12px', textAlign: 'center', fontWeight: 600, borderBottom: '2px solid #E0E0E0' }}>
                Status
              </th>
            </tr>
          </thead>
          <tbody>
            {sortedPlanets.map((planet, index) => (
              <tr
                key={planet.name}
                style={{
                  background: index % 2 === 0 ? '#FFFFFF' : '#FAFAFA',
                  borderBottom: '1px solid #E0E0E0',
                }}
              >
                {/* Planet name with symbol */}
                <td style={{ padding: '12px 16px', fontWeight: 600 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <span style={{ fontSize: '1.2rem' }}>{getPlanetSymbol(planet.name)}</span>
                    {planet.name}
                  </Box>
                </td>

                {/* Sign */}
                <td style={{ padding: '12px 16px' }}>
                  {planet.sign}
                </td>

                {/* House */}
                <td style={{ padding: '12px 16px', textAlign: 'center' }}>
                  <Box>
                    <Box sx={{ fontWeight: 600 }}>{planet.house}</Box>
                    <Box sx={{ fontSize: '0.75rem', color: 'text.secondary', mt: 0.5 }}>
                      {getHouseName(planet.house)}
                    </Box>
                  </Box>
                </td>

                {/* Degree */}
                <td style={{ padding: '12px 16px', textAlign: 'right', fontFamily: 'monospace' }}>
                  {planet.degree.toFixed(2)}°
                </td>

                {/* Nakshatra */}
                <td style={{ padding: '12px 16px' }}>
                  {planet.nakshatra}
                </td>

                {/* Pada */}
                <td style={{ padding: '12px 16px', textAlign: 'center' }}>
                  {planet.pada}
                </td>

                {/* Dignity */}
                <td style={{ padding: '12px 16px' }}>
                  <Chip
                    size="sm"
                    color={getDignityColor(planet.dignity)}
                    variant="soft"
                  >
                    {planet.dignity}
                  </Chip>
                </td>

                {/* Status flags */}
                <td style={{ padding: '8px 12px', textAlign: 'center' }}>
                  <Box sx={{ display: 'flex', gap: 0.5, justifyContent: 'center' }}>
                    {planet.isRetrograde && (
                      <Chip size="sm" color="warning" variant="outlined">
                        R
                      </Chip>
                    )}
                    {planet.isCombust && (
                      <Chip size="sm" color="danger" variant="outlined">
                        C
                      </Chip>
                    )}
                  </Box>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </Sheet>

      {/* Legend */}
      <Card variant="soft" sx={{ mt: 3, p: 1.5 }}>
        <Typography level="title-sm" sx={{ mb: 1.5, color: 'primary.main' }}>Legend</Typography>
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1.5, fontSize: '0.75rem', mb: 1 }}>
          <Box><strong>R</strong> = Retrograde &nbsp; <strong>C</strong> = Combust</Box>
        </Box>
        <Typography level="body-xs" sx={{ mb: 0.75, fontWeight: 600, color: 'text.secondary' }}>Dignity (planet’s relationship to current sign):</Typography>
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
          <Chip size="sm" color="success" variant="soft">Exalted</Chip>
          <Chip size="sm" color="primary" variant="soft">Own Sign</Chip>
          <Chip size="sm" color="primary" variant="soft">Moolatrikona</Chip>
          <Chip size="sm" color="neutral" variant="soft">Friend</Chip>
          <Chip size="sm" color="neutral" variant="soft">Neutral</Chip>
          <Chip size="sm" color="warning" variant="soft">Enemy</Chip>
          <Chip size="sm" color="danger" variant="soft">Debilitated</Chip>
        </Box>
      </Card>

      {/* Key insights */}
      <Card
        variant="outlined"
        sx={{
          mt: 3,
          p: 3,
          borderLeft: '4px solid',
          borderLeftColor: 'primary.main',
        }}
      >
        <Typography level="title-sm" sx={{ mb: 1.5, color: 'primary.main', display: 'flex', alignItems: 'center', gap: 1 }}>
          💡 Key Planetary Insights
        </Typography>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
          {/* Exalted planets */}
          {sortedPlanets.filter(p => p.dignity.toLowerCase() === 'exalted').length > 0 && (
            <Typography level="body-sm">
              <strong>Exalted Planets:</strong>{' '}
              {sortedPlanets
                .filter(p => p.dignity.toLowerCase() === 'exalted')
                .map(p => p.name)
                .join(', ')}{' '}
              - These planets operate at peak strength, bringing natural talents and favorable results.
            </Typography>
          )}

          {/* Debilitated planets */}
          {sortedPlanets.filter(p => p.dignity.toLowerCase() === 'debilitated').length > 0 && (
            <Typography level="body-sm">
              <strong>Debilitated Planets:</strong>{' '}
              {sortedPlanets
                .filter(p => p.dignity.toLowerCase() === 'debilitated')
                .map(p => p.name)
                .join(', ')}{' '}
              - These planets face challenges but can develop resilience through conscious effort.
            </Typography>
          )}

          {/* Retrograde planets */}
          {sortedPlanets.filter(p => p.isRetrograde).length > 0 && (
            <Typography level="body-sm">
              <strong>Retrograde Planets:</strong>{' '}
              {sortedPlanets
                .filter(p => p.isRetrograde)
                .map(p => p.name)
                .join(', ')}{' '}
              - Retrograde motion creates internalization of planetary energy, bringing karmic review and depth.
            </Typography>
          )}

          {/* Combust planets */}
          {sortedPlanets.filter(p => p.isCombust).length > 0 && (
            <Typography level="body-sm">
              <strong>Combust Planets:</strong>{' '}
              {sortedPlanets
                .filter(p => p.isCombust)
                .map(p => p.name)
                .join(', ')}{' '}
              - Combustion reduces planet's visibility and independent expression due to Sun's overpowering presence.
            </Typography>
          )}
        </Box>
      </Card>
    </ReportSection>
  );
};
